import { useCallback, useMemo, useState } from 'react'
import { apiUrl } from '../../api/client'
import { dia, shapes, defineCell } from '../../lib/joint'
import { layeredLayout, type LayoutEdge, type LayoutNode } from '../../lib/influenceLayout'
import { formatDate, initials } from '../../lib/format'
import { JointCanvas, token } from '../../components/JointCanvas'
import { Avatar } from '../../components/Avatar'
import type { AccountInfluence } from '../../types/accounts'
import type { ContactView } from '../../types/contacts'
import './accounts.css'

// One account's influence map as a graph: people are avatars (the photo when known, initials on a
// persona-coloured disc when not), sized by relationship strength, ringed by persona. The firm's own people
// are the roots on the left; the warm ties and everyone else known sit in the middle; the people to reach
// (the decision makers, and whoever the API routes to by introduction) are on the right. A direct warm tie
// is a solid edge; an introduction is a dashed arrow through the connector; a decision maker is emphasised;
// one nobody reaches wears a dashed "no path yet" halo; bridges to other accounts hang off the right edge.
// Positions come from lib/influenceLayout (pure, tested); colours are design tokens read at build time.

const CONNECTOR_PERSONAS = new Set(['champion', 'influencer', 'gatekeeper', 'challenger'])
const WARM = 0.3   // the API's warm band, mirrored only to draw a faint tie faintly
const LAYER_GAP = 340, NODE_GAP = 10, LABEL_W = 170, LABEL_W_BELOW = 132, MAX_MIDDLE = 24, MAX_TARGETS = 18
// a layer taller than this splits into side-by-side sub-columns so a busy account still fits the canvas
const PER_COLUMN = { connector: 8, target: 9 }, SUB_GAP = 150

// The avatar element: a halo (for "no path yet"), a persona ring, a disc with initials, and the photo clipped
// to the disc when there is one; the name and a sub line sit on the side the layer leaves free.
const PersonNode = dia.Element.define('scout.Person', {
  attrs: {
    halo: { cx: 'calc(w/2)', cy: 'calc(h/2)', r: 'calc(w/2+7)', fill: 'none', stroke: 'none', strokeWidth: 2, strokeDasharray: '5 4' },
    ring: { cx: 'calc(w/2)', cy: 'calc(h/2)', r: 'calc(w/2)', fill: 'none', strokeWidth: 2.5 },
    disc: { cx: 'calc(w/2)', cy: 'calc(h/2)', r: 'calc(w/2-3)' },
    clipCircle: { cx: 'calc(w/2)', cy: 'calc(h/2)', r: 'calc(w/2-3)' },
    photo: { x: 3, y: 3, width: 'calc(w-6)', height: 'calc(h-6)', preserveAspectRatio: 'xMidYMid slice' },
    initials: { x: 'calc(w/2)', y: 'calc(h/2)', textAnchor: 'middle', textVerticalAnchor: 'middle', fontWeight: 700, fontFamily: 'inherit' },
    name: { fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit' },
    sub: { fontSize: 10.5, fontFamily: 'inherit' },
    flag: { r: 5, strokeWidth: 1.5 },
  },
}, {
  markup: [
    { tagName: 'circle', selector: 'halo' },
    { tagName: 'circle', selector: 'ring' },
    { tagName: 'circle', selector: 'disc' },
    { tagName: 'clipPath', selector: 'clip', children: [{ tagName: 'circle', selector: 'clipCircle' }] },
    { tagName: 'image', selector: 'photo' },
    { tagName: 'text', selector: 'initials' },
    { tagName: 'text', selector: 'name' },
    { tagName: 'text', selector: 'sub' },
    { tagName: 'circle', selector: 'flag' },
  ],
})
defineCell('Person', PersonNode)

type Side = 'left' | 'right' | 'below'

export function AccountInfluenceMap({ contacts, influence, ownerNames, onOpen }: {
  contacts: ContactView[]; influence: AccountInfluence; ownerNames: Record<string, string>; onOpen: (personId: string) => void
}) {
  const [showBridges, setShowBridges] = useState(true)
  const people = useMemo(() => contacts.filter((p) => p.person.role !== 'colleague'), [contacts])
  const byId = useMemo(() => new Map(people.map((p) => [p.person.personId, p])), [people])
  const tie = useMemo(() => new Map(influence.ties.map((t) => [t.personId, t])), [influence.ties])
  const colleagueByName = useMemo(() => new Map(contacts.filter((c) => c.person.role === 'colleague').map((c) => [c.person.name.trim().toLowerCase(), c])), [contacts])

  const build = useCallback((model: any) => {
    const c = {
      ink: token('--ink'), ink2: token('--ink-2'), muted: token('--muted'), line: token('--line'), surface: token('--surface'), canvas: token('--canvas'),
      brand: token('--brand'), brandStrong: token('--brand-strong'), warn: token('--warn'), danger: token('--danger'), ok: token('--ok'),
    }
    const hue = (p: ContactView) => {
      const persona = p.person.persona; const role = p.person.role
      const name = persona && persona !== 'other' ? `--persona-${persona}` : role && role !== 'other' ? `--role-${role}` : '--persona-other'
      return token(name) || c.muted
    }
    const decision = new Set(influence.decisionMakerIds)
    const unreached = new Set(influence.unreachedIds)
    const introduced = new Set(influence.routes.filter((r) => r.viaPersonId).map((r) => r.targetPersonId))
    const bridged = new Set(influence.bridges.map((b) => b.personId))
    const byStrength = (a: ContactView, b: ContactView) => (b.person.relationshipStrength ?? 0) - (a.person.relationshipStrength ?? 0)

    // the layers follow the routes: targets are everyone the API says we reach through someone (or should: the
    // decision makers); the middle is everyone else known, warm ties first, faint ties after
    const targets = people.filter((p) => decision.has(p.person.personId) || introduced.has(p.person.personId))
      .sort((a, b) => (decision.has(b.person.personId) ? 1 : 0) - (decision.has(a.person.personId) ? 1 : 0) || byStrength(a, b)).slice(0, MAX_TARGETS)
    const middle = people.filter((p) => !decision.has(p.person.personId) && !introduced.has(p.person.personId))
      .sort((a, b) => byStrength(a, b) || (bridged.has(b.person.personId) ? 1 : 0) - (bridged.has(a.person.personId) ? 1 : 0)).slice(0, MAX_MIDDLE)
    const shown = new Map([...targets, ...middle].map((p) => [p.person.personId, p]))
    const ownerIds = [...new Set(people.map((p) => p.person.ownerId).filter((o): o is string => !!o))]
      .sort((a, b) => people.filter((p) => p.person.ownerId === b).length - people.filter((p) => p.person.ownerId === a).length)

    const disc = (p: ContactView) => {
      const s = p.person.relationshipStrength ?? 0
      return Math.round(28 + s * 28) + (decision.has(p.person.personId) ? 8 : 0)
    }
    const nodes: LayoutNode[] = [
      ...ownerIds.map((o) => ({ id: `owner:${o}`, layer: 'owner' as const, size: 56, rank: people.filter((p) => p.person.ownerId === o).length })),
      ...middle.map((p) => ({ id: p.person.personId, layer: 'connector' as const, size: disc(p) + 34, rank: (p.person.relationshipStrength ?? 0) * 100 })),
      ...targets.map((p) => ({ id: p.person.personId, layer: 'target' as const, size: Math.max(disc(p), 40), rank: (decision.has(p.person.personId) ? 1000 : 0) + (p.person.relationshipStrength ?? 0) * 100 })),
    ]
    const edges: LayoutEdge[] = []
    for (const p of shown.values()) if (p.person.ownerId) edges.push({ from: `owner:${p.person.ownerId}`, to: p.person.personId })
    for (const r of influence.routes) if (r.viaPersonId && shown.has(r.viaPersonId) && shown.has(r.targetPersonId)) edges.push({ from: r.viaPersonId, to: r.targetPersonId })
    const layout = layeredLayout(nodes, edges, { layerGap: LAYER_GAP, nodeGap: NODE_GAP, maxPerColumn: PER_COLUMN, subGap: SUB_GAP })
    const at = new Map(layout.nodes.map((n) => [n.id, n]))
    const top = 36

    const lane = (x: number, text: string, anchor: 'start' | 'end' | 'middle' = 'middle') => {
      new shapes.standard.Rectangle({ position: { x: x - 100, y: 0 }, size: { width: 200, height: 18 },
        attrs: { body: { fill: 'transparent', stroke: 'none' }, label: { text, fill: c.muted, fontSize: 10, fontWeight: 600, textAnchor: anchor, x: anchor === 'start' ? 0 : anchor === 'end' ? 200 : 100, letterSpacing: 1.2 } } }).addTo(model)
    }
    const [ownersCol, middleCol, targetsCol] = layout.columns
    const hidden = people.length - shown.size
    lane(ownersCol.x, 'THE FIRM', 'start')
    lane(middleCol.x, `KNOWN HERE · ${people.length - targets.length}${hidden > 0 ? ` · ${middle.length} DRAWN` : ''}`)
    lane(targetsCol.x, `TO REACH · ${targets.length}${decision.size ? ` · ${decision.size} decide` : ''}`)

    const cells = new Map<string, any>()
    const avatar = (id: string, x: number, y: number, d: number, opts: {
      name: string; sub: string; side: Side; hue: string; ringWidth?: number; photo?: string | null; discFill?: string; subFill?: string
      halo?: string; flag?: string; opacity?: number; bold?: boolean; personId?: string; title: string
    }) => {
      const clipId = `clip-${id.replace(/[^a-z0-9]/gi, '')}`
      const labelX = opts.side === 'left' ? -10 : opts.side === 'right' ? d + 10 : d / 2
      const anchor = opts.side === 'left' ? 'end' : opts.side === 'right' ? 'start' : 'middle'
      const nameY = opts.side === 'below' ? d + 14 : d / 2 - 6
      const subY = opts.side === 'below' ? d + 27 : d / 2 + 8
      const labelW = opts.side === 'below' ? LABEL_W_BELOW : LABEL_W
      const node = new PersonNode({
        position: { x: x - d / 2, y: top + y - d / 2 }, size: { width: d, height: d },
        attrs: {
          halo: { stroke: opts.halo ?? 'none' },
          ring: { stroke: opts.hue, strokeWidth: opts.ringWidth ?? 2.5 },
          disc: { fill: opts.discFill ?? opts.hue, opacity: opts.opacity ?? 1 },
          clip: { id: clipId },
          photo: opts.photo ? { xlinkHref: opts.photo, clipPath: `url(#${clipId})`, opacity: opts.opacity ?? 1 } : { display: 'none' },
          initials: opts.photo ? { display: 'none' } : { text: initials(opts.name), fill: c.surface, fontSize: Math.max(10, Math.round(d * 0.36)) },
          name: { text: opts.name, x: labelX, y: nameY, textAnchor: anchor, fill: opts.bold ? c.ink : c.ink2, fontWeight: opts.bold ? 700 : 600, textWrap: { width: labelW, maxLineCount: 1, ellipsis: true } },
          sub: { text: opts.sub, x: labelX, y: subY, textAnchor: anchor, fill: opts.subFill ?? c.muted, textWrap: { width: labelW, maxLineCount: 1, ellipsis: true } },
          flag: opts.flag ? { cx: d - 4, cy: d - 4, fill: opts.flag, stroke: c.surface } : { display: 'none' },
        },
      })
      ;(node as any).hoverId = id
      ;(node as any).title = opts.title
      if (opts.personId) (node as any).personId = opts.personId
      node.addTo(model); cells.set(id, node)
      return node
    }

    // the firm's people: the roots
    for (const o of ownerIds) {
      const pos = at.get(`owner:${o}`); if (!pos) continue
      const n = people.filter((p) => p.person.ownerId === o).length
      const name = ownerNames[o] ?? o
      const self = colleagueByName.get(name.trim().toLowerCase())
      avatar(`owner:${o}`, pos.x, pos.y, 46, { name, sub: `${n} tie${n === 1 ? '' : 's'} here`, side: 'left', hue: c.brand, ringWidth: 3, discFill: c.ink, photo: self?.person.photoUrl ? apiUrl(self.person.photoUrl) : null, bold: true, personId: self?.person.personId, title: `${name} · holds ${n} tie${n === 1 ? '' : 's'} at this account` })
    }
    if (ownerIds.length === 0) {
      new shapes.standard.Rectangle({ position: { x: ownersCol.x - 84, y: top + layout.height / 2 - 21 }, size: { width: 168, height: 42 },
        attrs: { body: { fill: c.canvas, stroke: c.line, strokeDasharray: '5 4', rx: 8, ry: 8 }, label: { text: 'No one at the firm\nholds a tie yet', fill: c.muted, fontSize: 11, lineHeight: 14 } } }).addTo(model)
    }

    // the people
    const person = (p: ContactView, side: Side) => {
      const id = p.person.personId
      const pos = at.get(id); if (!pos) return
      const s = p.person.relationshipStrength ?? 0
      const isDm = decision.has(id)
      const noPath = unreached.has(id)
      const dormant = p.signals.some((x) => x.type === 'dormant')
      const moved = p.signals.some((x) => x.type === 'job-change')
      const t = tie.get(id)
      const evidence = [t && t.touches > 0 ? `${t.touches} touch${t.touches === 1 ? '' : 'es'}` : null, t && t.meetings > 0 ? `${t.meetings} mtg${t.meetings === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')
      const sub = noPath ? 'no path yet' : moved ? 'may have moved' : dormant ? 'gone quiet' : p.person.title?.trim() || (evidence || `${Math.round(s * 100)}% tie`)
      const flags = [noPath ? 'no path yet' : '', dormant ? 'gone quiet' : '', moved ? 'may have moved' : ''].filter(Boolean).join(' · ')
      avatar(id, pos.x, pos.y, disc(p), {
        name: p.person.name, sub, side, hue: hue(p), ringWidth: isDm ? 3.5 : 2.5, photo: p.person.photoUrl ? apiUrl(p.person.photoUrl) : null,
        halo: noPath ? c.danger : undefined, flag: moved ? c.danger : dormant ? c.warn : undefined, bold: isDm,
        subFill: noPath || moved ? c.danger : dormant ? c.warn : undefined,
        opacity: s < WARM && !isDm && !CONNECTOR_PERSONAS.has(p.person.persona ?? '') ? 0.72 : 1, personId: id,
        title: `${p.person.name}${p.person.title ? ' · ' + p.person.title : ''}${p.person.persona ? ' · ' + p.person.persona : ''} · ${Math.round(s * 100)}%${flags ? ' · ' + flags : ''}`,
      })
    }
    middle.forEach((p) => person(p, 'below'))
    targets.forEach((p) => person(p, 'right'))
    if (targets.length === 0) {
      new shapes.standard.Rectangle({ position: { x: targetsCol.x - 100, y: top + layout.height / 2 - 21 }, size: { width: 200, height: 42 },
        attrs: { body: { fill: c.canvas, stroke: c.line, strokeDasharray: '5 4', rx: 8, ry: 8 }, label: { text: 'No decision maker\nidentified yet', fill: c.muted, fontSize: 11, lineHeight: 14 } } }).addTo(model)
    }
    // ties: owner to each person they know; weight is strength, a faint tie is dashed and grey
    const link = (from: any, to: any, attrs: Record<string, unknown>, z = -1) =>
      new shapes.standard.Link({ source: { id: from.id, anchor: { name: 'right' } }, target: { id: to.id, anchor: { name: 'left' } }, z, connector: { name: 'smooth' }, attrs: { line: attrs } }).addTo(model)
    for (const p of shown.values()) {
      const o = p.person.ownerId; if (!o) continue
      const from = cells.get(`owner:${o}`); const to = cells.get(p.person.personId)
      if (!from || !to) continue
      const s = p.person.relationshipStrength ?? 0
      const warm = s >= WARM
      const toDm = decision.has(p.person.personId)
      link(from, to, { stroke: warm ? (toDm ? c.brandStrong : c.brand) : c.line, strokeWidth: warm ? 1.2 + s * 3.2 : 1, strokeDasharray: warm ? 'none' : '3 4', opacity: warm ? 0.5 + s * 0.45 : 0.9, targetMarker: { type: 'none' } })
    }
    // introductions: the connector reaches the person the owner cannot
    for (const r of influence.routes) {
      if (!r.viaPersonId) continue
      const from = cells.get(r.viaPersonId); const to = cells.get(r.targetPersonId)
      if (!from || !to) continue
      link(from, to, { stroke: c.brandStrong, strokeWidth: 2, strokeDasharray: '7 4', targetMarker: { type: 'path', d: 'M 10 -5 0 0 10 5 z', fill: c.brandStrong, stroke: c.brandStrong } }, 1)
    }
    // bridges: the subtle overlay off the right edge
    if (showBridges && influence.bridges.length > 0) {
      const bx = targetsCol.x + (targetsCol.subColumns - 1) * SUB_GAP / 2 + LABEL_W + 70
      lane(bx + 100, `BRIDGES · ${influence.bridges.length}`)
      const seen = new Map<string, number>()
      let by = top
      for (const b of influence.bridges) {
        const from = cells.get(b.personId); if (!from) continue
        if ((seen.get(b.personId) ?? 0) >= 2) continue
        seen.set(b.personId, (seen.get(b.personId) ?? 0) + 1)
        const text = b.kind === 'account' ? `${b.companyName} · ${b.affiliation}` : b.kind === 'person' ? `${b.bridgePersonName} at ${b.companyName}` : `writes from ${b.affiliation}`
        const tag = new shapes.standard.Rectangle({ position: { x: bx, y: by }, size: { width: 200, height: 26 },
          attrs: { body: { fill: c.surface, stroke: c.ok, strokeWidth: 1, strokeDasharray: '2 3', rx: 13, ry: 13, opacity: 0.9 },
            label: { text, fill: c.ink2, fontSize: 10.5, textAnchor: 'start', x: 10, textWrap: { width: 184, maxLineCount: 1, ellipsis: true } } } })
        ;(tag as any).title = `Bridge to ${b.companyName}, an account we know: ${b.kind === 'account' ? 'an affiliation names it' : b.kind === 'person' ? 'a shared affiliation with ' + b.bridgePersonName : 'this person writes from its domain'}`
        if (b.bridgePersonId) (tag as any).personId = b.bridgePersonId
        tag.addTo(model)
        new shapes.standard.Link({ source: { id: from.id, anchor: { name: 'right' } }, target: { id: tag.id, anchor: { name: 'left' } }, z: -2, connector: { name: 'smooth' },
          attrs: { line: { stroke: c.ok, strokeWidth: 1, strokeDasharray: '2 4', opacity: 0.7, targetMarker: { type: 'none' } } } }).addTo(model)
        by += 32
      }
    }
  }, [people, influence, ownerNames, showBridges, tie, colleagueByName])

  const hoverCard = useCallback((id: string) => {
    if (id.startsWith('owner:')) {
      const o = id.slice(6)
      const n = people.filter((p) => p.person.ownerId === o).length
      const warm = people.filter((p) => p.person.ownerId === o && (p.person.relationshipStrength ?? 0) >= WARM).length
      return <div className="hover-person"><Avatar name={ownerNames[o] ?? o} size={40} ring="brand" /><div><strong>{ownerNames[o] ?? o}</strong><span className="muted small">the firm · {n} tie{n === 1 ? '' : 's'} here, {warm} warm</span></div></div>
    }
    const p = byId.get(id); if (!p) return null
    const t = tie.get(id)
    const pct = Math.round((p.person.relationshipStrength ?? 0) * 100)
    const dormant = p.signals.some((x) => x.type === 'dormant'); const moved = p.signals.some((x) => x.type === 'job-change')
    return (
      <div className="hover-person">
        <Avatar name={p.person.name} photoUrl={p.person.photoUrl} persona={p.person.persona} role={p.person.role} size={44} />
        <div>
          <strong>{p.person.name}</strong>
          {p.person.title && <span className="small">{p.person.title}</span>}
          <span className="muted small">{[p.person.persona && p.person.persona !== 'other' ? p.person.persona : null, p.person.role && p.person.role !== 'other' ? p.person.role : null].filter(Boolean).join(' · ') || 'no persona yet'}</span>
          <dl className="hover-facts">
            <dt>Tie</dt><dd>{pct}%{influence.unreachedIds.includes(id) ? ' · no path yet' : ''}</dd>
            <dt>Last touch</dt><dd>{t?.lastTouch ? formatDate(t.lastTouch) : p.lastActivity ? formatDate(p.lastActivity) : '—'}</dd>
            <dt>Meetings</dt><dd>{t?.meetings ?? 0}{t && t.touches > 0 ? ` · ${t.touches} touch${t.touches === 1 ? '' : 'es'}` : ''}</dd>
            {(dormant || moved) && <><dt>Watch</dt><dd className="warn">{[dormant ? 'gone quiet' : '', moved ? 'may have moved' : ''].filter(Boolean).join(' · ')}</dd></>}
          </dl>
          <span className="muted small">click to open</span>
        </div>
      </div>
    )
  }, [people, byId, tie, ownerNames, influence.unreachedIds])

  return (
    <JointCanvas
      build={build}
      deps={[build]}
      onOpen={onOpen}
      hoverCard={hoverCard}
      className="account-map"
      toolbar={influence.bridges.length > 0 ? (
        <button className={`button small ${showBridges ? 'on' : ''}`} onClick={() => setShowBridges((v) => !v)} title="Bridges: people here affiliated with other accounts we know.">
          {showBridges ? 'Hide bridges' : `Bridges (${influence.bridges.length})`}
        </button>
      ) : undefined}
      legend={
        <>
          <span><i className="lg-owner" />the firm</span>
          <span><i className="lg-dm" />decision maker</span>
          <span><i className="lg-champion" />champion</span>
          <span><i className="lg-influencer" />influencer</span>
          <span><i className="lg-gate" />gatekeeper / challenger</span>
          <span><i className="lg-other" />no persona yet</span>
          <span><b className="lg-tie" />tie (weight = strength)</span>
          <span><b className="lg-intro" />introduction</span>
          <span><i className="lg-halo" />no path yet</span>
          <span><b className="lg-bridge" />bridge</span>
          <span className="muted small">size = strength · photo when known</span>
        </>
      }
    />
  )
}
